import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { api } from "../services/api";
import { toast, ToastContainer } from "react-toastify";

const ExamResultsLayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const examId = searchParams.get("examId");

  const [results, setResults] = useState([]);
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "completedAt",
    direction: "desc",
  });

  // Fetch exam details and results
  useEffect(() => {
    const fetchExamAndResults = async () => {
      if (!examId) {
        setError("No exam ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch exam details
        const examResponse = await api.exams.getById(examId);
        if (!examResponse.success) {
          setError(examResponse.message || "Failed to fetch exam details");
          setLoading(false);
          return;
        }

        setExam(examResponse.data);

        // Fetch exam results
        const resultsResponse = await api.exams.getResultsByExamId(examId);
        if (!resultsResponse.success) {
          setError(resultsResponse.message || "Failed to fetch exam results");
          setLoading(false);
          return;
        }

        setResults(resultsResponse.data);
      } catch (err) {
        setError("Error connecting to the server");
        console.error("Error fetching exam results:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchExamAndResults();
  }, [examId]);

  // Sort results
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Get sorted results
  const getSortedResults = () => {
    const sortableResults = [...results];
    if (sortConfig.key) {
      sortableResults.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nested properties (e.g., user.firstName)
        if (sortConfig.key.includes(".")) {
          const keys = sortConfig.key.split(".");
          aValue = keys.reduce((obj, key) => obj && obj[key], a);
          bValue = keys.reduce((obj, key) => obj && obj[key], b);
        }

        // Handle date strings
        if (sortConfig.key === "completedAt") {
          return sortConfig.direction === "asc"
            ? new Date(aValue) - new Date(bValue)
            : new Date(bValue) - new Date(aValue);
        }

        // Handle strings
        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortConfig.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        // Handle numbers
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      });
    }
    return sortableResults;
  };

  // Filter results by search term
  const getFilteredResults = () => {
    const sortedResults = getSortedResults();

    if (!searchTerm.trim()) {
      return sortedResults;
    }

    const term = searchTerm.toLowerCase();
    return sortedResults.filter((result) => {
      // Search in user name and email
      if (
        result.user.firstName?.toLowerCase().includes(term) ||
        result.user.lastName?.toLowerCase().includes(term) ||
        result.user.email?.toLowerCase().includes(term)
      ) {
        return true;
      }
      // Search in score
      if (result.score.toString().includes(term)) {
        return true;
      }
      // Search in pass/fail status
      if (
        (term === "pass" && result.passed) ||
        (term === "fail" && !result.passed)
      ) {
        return true;
      }
      return false;
    });
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Format duration
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Render sort icon
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <Icon icon="akar-icons:arrow-up-down" className="ms-1" />;
    }
    return sortConfig.direction === "asc" ? (
      <Icon icon="akar-icons:arrow-up" className="ms-1" />
    ) : (
      <Icon icon="akar-icons:arrow-down" className="ms-1" />
    );
  };

  return (
    <div className="container-fluid">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between align-items-center">
              <div>
                <h6 className="text-lg fw-semibold mb-0">
                  {exam ? `Results: ${exam.title}` : "Exam Results"}
                </h6>
                {exam && (
                  <p className="text-muted mb-0 mt-1">
                    {exam.description
                      ? exam.description
                      : `${exam.difficulty} level exam, Pass: ${exam.passPercentage}%`}
                  </p>
                )}
              </div>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => navigate("/all-exams")}
              >
                <Icon icon="akar-icons:arrow-left" className="me-2" />
                Back to All Exams
              </button>
            </div>
            <div className="card-body p-24">
              {/* Search bar */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">
                      <Icon icon="ion:search-outline" />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name, email, or score..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6 text-md-end">
                  {results.length > 0 && (
                    <p className="mb-0 mt-2 mt-md-0">
                      <strong>{getFilteredResults().length}</strong> results
                      found
                    </p>
                  )}
                </div>
              </div>

              {/* Loading and error states */}
              {loading && (
                <div className="d-flex justify-content-center my-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {/* Results Table */}
              {!loading && !error && results.length === 0 && (
                <div className="alert alert-info" role="alert">
                  No results found for this exam.
                </div>
              )}

              {!loading && !error && results.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="bg-base">
                      <tr>
                        <th
                          scope="col"
                          className="px-3 py-2 fw-semibold"
                          onClick={() => requestSort("user.firstName")}
                          style={{ cursor: "pointer" }}
                        >
                          User {renderSortIcon("user.firstName")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 fw-semibold"
                          onClick={() => requestSort("user.email")}
                          style={{ cursor: "pointer" }}
                        >
                          Email {renderSortIcon("user.email")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 fw-semibold"
                          onClick={() => requestSort("score")}
                          style={{ cursor: "pointer" }}
                        >
                          Score {renderSortIcon("score")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 fw-semibold"
                          onClick={() => requestSort("passed")}
                          style={{ cursor: "pointer" }}
                        >
                          Status {renderSortIcon("passed")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 fw-semibold"
                          onClick={() => requestSort("timeSpent")}
                          style={{ cursor: "pointer" }}
                        >
                          Duration {renderSortIcon("timeSpent")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 fw-semibold"
                          onClick={() => requestSort("completedAt")}
                          style={{ cursor: "pointer" }}
                        >
                          Completed {renderSortIcon("completedAt")}
                        </th>
                        <th scope="col" className="px-3 py-2 fw-semibold">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredResults().map((result) => (
                        <tr key={result.id}>
                          <td className="px-3 py-2">
                            {result.user.firstName} {result.user.lastName}
                          </td>
                          <td className="px-3 py-2">{result.user.email}</td>
                          <td className="px-3 py-2">{result.score}%</td>
                          <td className="px-3 py-2">
                            <span
                              className={`badge ${
                                result.passed ? "bg-success" : "bg-danger"
                              }`}
                            >
                              {result.passed ? "Passed" : "Failed"}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {formatDuration(result.timeSpent)}
                          </td>
                          <td className="px-3 py-2">
                            {formatDate(result.completedAt)}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() =>
                                navigate(
                                  `/exam-result-details?resultId=${result.id}`
                                )
                              }
                              title="View Details"
                            >
                              <Icon icon="akar-icons:eye" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Summary Section */}
              {!loading && !error && results.length > 0 && (
                <div className="mt-4">
                  <h6 className="fw-semibold mb-3">Summary</h6>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <div className="card p-3 bg-light-subtle">
                        <h5 className="fw-bold mb-0">{results.length}</h5>
                        <small className="text-muted">Total Attempts</small>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card p-3 bg-success-subtle">
                        <h5 className="fw-bold mb-0">
                          {results.filter((r) => r.passed).length}
                        </h5>
                        <small className="text-muted">Passed</small>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card p-3 bg-danger-subtle">
                        <h5 className="fw-bold mb-0">
                          {results.filter((r) => !r.passed).length}
                        </h5>
                        <small className="text-muted">Failed</small>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card p-3 bg-primary-subtle">
                        <h5 className="fw-bold mb-0">
                          {Math.round(
                            results.reduce((acc, r) => acc + r.score, 0) /
                              results.length
                          )}
                          %
                        </h5>
                        <small className="text-muted">Average Score</small>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamResultsLayer;
